import React, { createContext, useContext, useEffect, useState } from 'react';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { UserApi, AuthApi, UserProfile as User } from '@/services/api';

interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isAuthenticated: boolean;
  isLoading: boolean;
  handleLoginSuccess: (loginResponse: { access_token: string, user: User }) => void;
  logout: (options?: { navigate?: boolean }) => void;
  register: (username: string, email: string, password: string, first_name?: string, last_name?: string) => Promise<{ success: boolean; error?: string }>;
  verifyEmail: (token: string) => Promise<boolean>;
  resetPassword: (token: string, password: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<boolean>;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  // Handle token expired events
  useEffect(() => {
    const handleTokenExpired = () => {
      // Clear user data
      setUser(null);
      
      // Clear React Query cache
      if (queryClient) {
        queryClient.clear();
      }
      
      // Show toast message
      toast({
        title: "Session Expired",
        description: "Your session has expired. Please log in again.",
        duration: 5000
      });
    };
    
    // Listen for the custom token-expired event
    window.addEventListener('token-expired', handleTokenExpired);
    
    return () => {
      window.removeEventListener('token-expired', handleTokenExpired);
    };
  }, [toast, queryClient]);
  
  // Check if user is already logged in (on component mount)
  useEffect(() => {
    let isMounted = true;
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (isMounted) setIsLoading(false);
        return;
      }
      
      try {
        // Use the UserApi.getCurrentUser which uses fetchWithRetry
        const userData = await UserApi.getCurrentUser();
        if (isMounted) {
          setUser(userData);
        }
      } catch (error) {
        // Errors (including 401 after failed refresh) are handled by fetchWithRetry/handleApiError
        // which dispatches 'token-expired' event. The listener above handles UI update.
        console.error('Authentication check failed:', error);
        // No need to setUser(null) here, event listener does it.
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    
    checkAuthStatus();
    
    return () => { isMounted = false; };
  }, []);
  
  // Function to handle successful login data
  const handleLoginSuccess = (loginResponse: { access_token: string, user: User }) => {
    const { access_token: token, user: userData } = loginResponse;
    localStorage.setItem('token', token);
    queryClient.invalidateQueries(); // Invalidate queries instead of clearing all cache
    setUser(userData); // Update user state
    setIsLoading(false); // Mark loading as complete
  };
  
  // Logout function
  const logout = async (options?: { navigate?: boolean }) => {
    const navigateAfterLogout = options?.navigate ?? true;

    // Call Backend Logout First 
    try {
      await AuthApi.logout(); 
    } catch (error) {
      // Error logged within AuthApi.logout/fetchWithRetry
    }
    
    // --- Client-side cleanup (always run) --- 
    localStorage.removeItem('token');
    // Remove queries instead of invalidating to prevent immediate refetch attempts
    queryClient.removeQueries(); // Removes all queries from the cache
    setUser(null); 
    // --- End Client-side cleanup ---
    
    if (navigateAfterLogout) {
      navigate('/login');
    }
  };
  
  // Register function
  const register = async (username: string, email: string, password: string, first_name?: string, last_name?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, email, password, first_name, last_name })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Registration Successful',
          description: 'Please check your email to verify your account.',
        });
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'An unexpected network error occurred. Please try again.' };
    }
  };
  
  // Email verification function
  const verifyEmail = async (token: string): Promise<boolean> => {
    try {
      const response = await fetch(`/api/auth/verify-email/${token}`, {
        method: 'GET'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Email Verified',
          description: 'Your email has been successfully verified.',
          type: 'success'
        });
        return true;
      } else {
        toast({
          title: 'Verification Failed',
          description: data.error || 'Failed to verify email',
          type: 'error'
        });
        return false;
      }
    } catch (error) {
      console.error('Email verification error:', error);
      toast({
        title: 'Verification Error',
        description: 'An unexpected error occurred. Please try again.',
        type: 'error'
      });
      return false;
    }
  };
  
  // Request password reset function
  const requestPasswordReset = async (email: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/reset-password-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Reset Link Sent',
          description: 'Check your email for password reset instructions.',
          type: 'success'
        });
        return true;
      } else {
        toast({
          title: 'Request Failed',
          description: data.error || 'Failed to request password reset',
          type: 'error'
        });
        return false;
      }
    } catch (error) {
      console.error('Password reset request error:', error);
      toast({
        title: 'Request Error',
        description: 'An unexpected error occurred. Please try again.',
        type: 'error'
      });
      return false;
    }
  };
  
  // Reset password function
  const resetPassword = async (token: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Password Reset',
          description: 'Your password has been reset successfully.',
          type: 'success'
        });
        return true;
      } else {
        toast({
          title: 'Reset Failed',
          description: data.error || 'Failed to reset password',
          type: 'error'
        });
        return false;
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast({
        title: 'Reset Error',
        description: 'An unexpected error occurred. Please try again.',
        type: 'error'
      });
      return false;
    }
  };
  
  // Provide the authentication context to children components
  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      isAuthenticated: !!user,
      isLoading,
      handleLoginSuccess,
      logout,
      register,
      verifyEmail,
      resetPassword,
      requestPasswordReset
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider; 